import { useState, useEffect, useRef } from "react";
import { catalogueItemApi, cataloguePhotoApi, toAbsUrl } from "../api/api";
import "./common.css";
import { useResizableColumns } from "../hooks/useResizableColumns";
import SearchableDropdown from "../components/SearchableDropdown";
import RvDataCard, { RvDataCardList } from "../components/RvDataCard";

/* ══════════════════════════════════════
   SHARED MICRO-COMPONENTS
══════════════════════════════════════ */

function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3400); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`rv-toast rv-toast--${type}`}>
      <span className="rv-toast-icon">{type === "success" ? <CheckIcon /> : <AlertIcon />}</span>
      <span className="rv-toast-msg">{message}</span>
      <button className="rv-toast-close" onClick={onClose}><CloseIcon /></button>
    </div>
  );
}

function ConfirmDialog({ name, onConfirm, onCancel }) {
  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog">
        <div className="rv-dialog-icon"><TrashIcon /></div>
        <h3 className="rv-dialog-title">Delete Item?</h3>
        <p className="rv-dialog-desc"><strong>{name}</strong> and all its photos will be permanently removed.</p>
        <div className="rv-dialog-actions">
          <button className="rv-btn rv-btn--ghost" onClick={onCancel}>Cancel</button>
          <button className="rv-btn rv-btn--danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   PHOTO MANAGER (per catalogue item)
══════════════════════════════════════ */
function PhotoManager({ itemID, photos, onPhotosChange, showToast }) {
  const [uploading, setUploading] = useState(false);
  const [deleting,  setDeleting]  = useState(null);
  const [editPhoto, setEditPhoto] = useState(null);
  const fileRef                   = useRef();

  /* ── Photo src resolver ── */
  const getPhotoSrc = (ph) => {
    if (!ph) return null;
    const rawPath =
      ph.filepath ||
      ph.Filepath ||
      ph.filename ||
      ph.Filename ||
      (Array.isArray(ph.photos) ? ph.photos[0] : ph.photos) ||
      (Array.isArray(ph.Photos) ? ph.Photos[0] : ph.Photos);
    return rawPath && typeof rawPath === "string" ? toAbsUrl(rawPath) : null;
  };

  /* ── Upload new photos ── */
const handleFiles = async (files) => {
  if (!files?.length) return;
  setUploading(true);
  try {
    await cataloguePhotoApi.upload({
      catalogueItemID: itemID,
      files,
      comments:     "",
      displayOrder: photos.length + 1,
      isPrimary:    photos.length === 0,
    });
    showToast(`${files.length} photo(s) uploaded.`);
    onPhotosChange();
  } catch (err) {
    showToast(err.message || "Upload failed.", "error");
  } finally {
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  }
};

  /* ── Delete photo ── */
  const handleDeletePhoto = async (photoID) => {
    setDeleting(photoID);
    try {
      await cataloguePhotoApi.remove(photoID);
      showToast("Photo removed.");
      onPhotosChange();
    } catch (err) {
      showToast(err.message || "Delete failed.", "error");
    } finally {
      setDeleting(null);
    }
  };

  /* ── Update photo meta ── */
  const handleUpdatePhoto = async () => {
    if (!editPhoto) return;
    try {
      await cataloguePhotoApi.update({
        photoID:         editPhoto.photo.photoID,
        catalogueItemID: itemID,
        filepath:        editPhoto.photo.filepath ?? "",
        filename:        editPhoto.photo.filename ?? "",
        comments:        editPhoto.form.comments,
        displayOrder:    Number(editPhoto.form.displayOrder),
        isPrimary:       editPhoto.form.isPrimary,
      });
      showToast("Photo updated.");
      setEditPhoto(null);
      onPhotosChange();
    } catch (err) {
      showToast(err.message || "Update failed.", "error");
    }
  };

  return (
    <div className="rv-pm">
      {/* Upload zone */}
      <div
        className={`rv-pm-drop ${uploading ? "rv-pm-drop--loading" : ""}`}
        onClick={() => !uploading && fileRef.current?.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
      >
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: "none" }}
          onChange={e => handleFiles(e.target.files)}
        />
        {uploading ? (
          <><div className="rv-pm-spinner" /><span>Uploading…</span></>
        ) : (
          <><UploadIcon /><span>Drop images here or <strong>click to browse</strong></span></>
        )}
      </div>

      {/* Photo grid */}
      {photos.length === 0 ? (
        <p className="rv-pm-empty">No photos yet. Upload above to get started.</p>
      ) : (
        <div className="rv-pm-grid">
          {photos
            .slice()
            .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
            .map(ph => (
              <div key={ph.photoID} className={`rv-pm-thumb ${ph.isPrimary ? "rv-pm-thumb--primary" : ""}`}>

                {/* Image preview */}
                {getPhotoSrc(ph) ? (
                  <img
                    src={getPhotoSrc(ph)}
                    alt={ph.filename || "photo"}
                    className="rv-pm-img"
                    onError={e => { e.target.style.display = "none"; }}
                  />
                ) : (
                  <div className="rv-pm-img-placeholder"><ImageIcon /></div>
                )}

                {/* Badges */}
                <div className="rv-pm-badges">
                  {ph.isPrimary && <span className="rv-pm-badge rv-pm-badge--primary">Primary</span>}
                  <span className="rv-pm-badge rv-pm-badge--order">#{ph.displayOrder ?? "—"}</span>
                </div>

                {/* Actions */}
                <div className="rv-pm-actions">
                  <button
                    className="rv-pm-btn rv-pm-btn--edit"
                    title="Edit"
                    onClick={() => setEditPhoto({
                      photo: ph,
                      form: {
                        comments:     ph.comments     ?? "",
                        displayOrder: ph.displayOrder ?? 1,
                        isPrimary:    ph.isPrimary    ?? false,
                      },
                    })}
                  ><EditIcon /></button>
                  <button
                    className="rv-pm-btn rv-pm-btn--del"
                    title="Delete"
                    disabled={deleting === ph.photoID}
                    onClick={() => handleDeletePhoto(ph.photoID)}
                  >
                    {deleting === ph.photoID ? <div className="rv-pm-mini-spin" /> : <TrashIcon />}
                  </button>
                </div>

                {ph.comments && <p className="rv-pm-caption">{ph.comments}</p>}
              </div>
            ))}
        </div>
      )}

      {/* Edit photo modal */}
      {editPhoto && (
        <div className="rv-dialog-backdrop">
          <div className="rv-dialog rv-dialog--form rv-dialog--sm">
            <div className="rv-modal-header">
              <div className="rv-modal-icon"><ImageIcon /></div>
              <div>
                <h3 className="rv-modal-title">Edit Photo</h3>
                <p className="rv-modal-sub">{editPhoto.photo.filename || `Photo #${editPhoto.photo.photoID}`}</p>
              </div>
              <button className="rv-modal-close" onClick={() => setEditPhoto(null)}><CloseIcon /></button>
            </div>
            <div className="rv-modal-body">
              <div className="rv-field">
                <label className="rv-label">Comments / Caption</label>
                <input
                  className="rv-input"
                  placeholder="e.g. Front view"
                  value={editPhoto.form.comments}
                  onChange={e => setEditPhoto(p => ({ ...p, form: { ...p.form, comments: e.target.value } }))}
                />
              </div>
              <div className="rv-field-row-2col">
                <div className="rv-field">
                  <label className="rv-label">Display Order</label>
                  <input
                    className="rv-input"
                    type="number"
                    min="1"
                    value={editPhoto.form.displayOrder}
                    onChange={e => setEditPhoto(p => ({ ...p, form: { ...p.form, displayOrder: e.target.value } }))}
                  />
                </div>
                <div className="rv-field rv-field--row">
                  <div>
                    <label className="rv-label">Primary Photo</label>
                    <p className="rv-field-hint">Shown as cover image</p>
                  </div>
                  <button
                    type="button"
                    className={`rv-toggle ${editPhoto.form.isPrimary ? "rv-toggle--on" : ""}`}
                    onClick={() => setEditPhoto(p => ({ ...p, form: { ...p.form, isPrimary: !p.form.isPrimary } }))}
                  ><span className="rv-toggle-knob" /></button>
                </div>
              </div>
            </div>
            <div className="rv-modal-footer">
              <button className="rv-btn rv-btn--ghost" onClick={() => setEditPhoto(null)}>Cancel</button>
              <button className="rv-btn rv-btn--primary" onClick={handleUpdatePhoto}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════
   RICH TEXT EDITOR (QUILL) FOR CATALOGUE
══════════════════════════════════════ */
function CatalogueQuillEditor({ initialHtml, onChange, placeholder }) {
  const containerRef = useRef(null);
  const quillRef     = useRef(null);
  const onChangeRef  = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!containerRef.current || quillRef.current) return;
    if (!window.Quill) {
      console.error("Quill not loaded — ensure CDN links are present in public/index.html.");
      return;
    }

    const quill = new window.Quill(containerRef.current, {
      theme: "snow",
      placeholder: placeholder || "Write item description here…",
      modules: {
        toolbar: [
          [{ size: ["small", false, "large", "huge"] }],
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ color: [] }, { background: [] }],
          [{ align: [] }],
          [{ list: "ordered" }, { list: "bullet" }],
          ["blockquote"],
          ["link"],
          ["clean"],
        ],
      },
    });

    if (initialHtml && initialHtml.trim() && initialHtml !== "<p><br></p>") {
      if (/<[a-z][\s\S]*>/i.test(initialHtml)) {
        quill.clipboard.dangerouslyPasteHTML(initialHtml);
      } else {
        quill.setText(initialHtml.trim() + "\n");
      }
    }

    quill.on("text-change", () => {
      const isEmpty = quill.getText().trim().length === 0;
      const html = isEmpty ? "" : quill.root.innerHTML;
      if (onChangeRef.current) {
        onChangeRef.current(html);
      }
    });

    quillRef.current = quill;

    return () => {
      const toolbar = quill.getModule("toolbar");
      if (toolbar?.container) toolbar.container.remove();
      if (containerRef.current) containerRef.current.innerHTML = "";
      quillRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        minHeight: 140,
        fontSize: 13.5,
        fontFamily: "var(--b-font, sans-serif)",
      }}
    />
  );
}

/* ══════════════════════════════════════
   CATALOGUE ITEM FORM MODAL
══════════════════════════════════════ */
function CatalogueModal({ initial, products, onSave, onClose, saving, showToast }) {
  const isEdit = !!initial?.catalogueItemID;

  const [form, setForm] = useState({
    productID: initial?.productID != null ? String(initial.productID) : "",
    displayName: initial?.displayName ?? "",
    descriptionLbl: initial?.descriptionLbl ?? "",
    description: initial?.description ?? "",
    comments: initial?.comments ?? "",
    displaySection: initial?.displaySection ?? "",
    isFeatured: initial?.isFeatured ?? false,
    isActive: initial?.isActive ?? true,
  });
  const [errors, setErrors] = useState({});
  const [pendingFiles, setPendingFiles] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const fileInputRef = useRef();

  useEffect(() => {
    if (isEdit && initial?.catalogueItemID) {
      setLoadingPhotos(true);
      cataloguePhotoApi.getByItemId(initial.catalogueItemID)
        .then(data => setExistingPhotos(Array.isArray(data) ? data : data?.data ?? []))
        .catch(() => setExistingPhotos([]))
        .finally(() => setLoadingPhotos(false));
    }
  }, [isEdit, initial?.catalogueItemID]);

  const set = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    setErrors(e => ({ ...e, [key]: "" }));
  };

  const handleFileSelect = (files) => {
    if (!files || !files.length) return;
    const newFiles = Array.from(files);
    setPendingFiles(prev => [...prev, ...newFiles]);
  };

  const removePendingFile = (idx) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleDeleteExisting = async (photoID) => {
    try {
      await cataloguePhotoApi.remove(photoID);
      setExistingPhotos(prev => prev.filter(p => p.photoID !== photoID));
      if (showToast) showToast("Photo removed.");
    } catch (err) {
      if (showToast) showToast(err.message || "Failed to remove photo.", "error");
    }
  };

  const validate = () => {
    const e = {};
    if (!form.productID) e.productID = "Please select a product.";
    if (!form.displayName.trim()) e.displayName = "Display name is required.";
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave(
      {
        ...initial,
        productID: Number(form.productID),
        displayName: form.displayName.trim(),
        descriptionLbl: form.descriptionLbl.trim(),
        description: form.description === "<p><br></p>" ? "" : form.description.trim(),
        comments: form.comments.trim(),
        displaySection: form.displaySection.trim(),
        isFeatured: form.isFeatured,
        isActive: form.isActive,
      },
      pendingFiles
    );
  };

  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog rv-dialog--form rv-dialog--wide" style={{ maxWidth: 720, maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <div className="rv-modal-header">
          <div className="rv-modal-icon"><CatalogueIcon /></div>
          <div>
            <h3 className="rv-modal-title">{isEdit ? "Edit Catalogue Item" : "New Catalogue Item"}</h3>
            <p className="rv-modal-sub">
              {isEdit ? "Update catalogue details and photos below." : "Fill in details and upload photos to add a product to the catalogue."}
            </p>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        <div className="rv-modal-body" style={{ overflowY: "auto", flex: 1, paddingRight: 16 }}>

          {/* Product */}
          <div className="rv-field">
            <label className="rv-label">Product <span className="rv-required">*</span></label>
            <SearchableDropdown
              options={products.map(p => ({
                ...p,
                label: p.productCode ? `${p.productName} (${p.productCode})` : p.productName
              }))}
              valueKey="productID"
              labelKey="label"
              value={form.productID}
              onChange={val => set("productID", val)}
              placeholder="Select product…"
              searchPlaceholder="Search products…"
              error={errors.productID}
              pageSize={10}
            />
          </div>

          {/* Display name + section */}
          <div className="rv-field-row-2col">
            <div className="rv-field">
              <label className="rv-label">Display Name <span className="rv-required">*</span></label>
              <input
                className={`rv-input ${errors.displayName ? "rv-input--err" : ""}`}
                placeholder="e.g. Royal Solitaire Ring"
                value={form.displayName}
                onChange={e => set("displayName", e.target.value)}
              />
              {errors.displayName && <p className="rv-field-err">{errors.displayName}</p>}
            </div>
            <div className="rv-field">
              <label className="rv-label">Display Section</label>
              <input
                className="rv-input"
                placeholder="e.g. Bridal Collection, Featured"
                value={form.displaySection}
                onChange={e => set("displaySection", e.target.value)}
              />
            </div>
          </div>

          {/* Photo Upload Zone */}
          <div className="rv-field" style={{ marginBottom: 14 }}>
            <label className="rv-label" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Photos & Gallery</span>
              <span style={{ fontSize: 11, color: "var(--taupe)", fontWeight: 400 }}>Supports JPG, PNG, WEBP</span>
            </label>

            <div
              className="rv-pm-drop"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => {
                e.preventDefault();
                handleFileSelect(e.dataTransfer.files);
              }}
              style={{ cursor: "pointer", border: "1.5px dashed var(--taupe)", borderRadius: 10, padding: "16px 12px", textAlign: "center", background: "rgba(185,167,146,.05)" }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={e => handleFileSelect(e.target.files)}
              />
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: "var(--dark)" }}>
                <UploadIcon />
                <span style={{ fontSize: 12.5 }}>
                  Drop photos here or <strong style={{ color: "var(--taupe-dark)" }}>click to browse</strong>
                </span>
              </div>
            </div>

            {/* Selected Pending Photos Preview */}
            {pendingFiles.length > 0 && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                {pendingFiles.map((file, idx) => (
                  <div key={idx} style={{ position: "relative", width: 64, height: 64, borderRadius: 8, overflow: "hidden", border: "1px solid var(--taupe)" }}>
                    <img
                      src={URL.createObjectURL(file)}
                      alt="preview"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    <button
                      type="button"
                      onClick={() => removePendingFile(idx)}
                      style={{
                        position: "absolute", top: 2, right: 2, background: "rgba(0,0,0,.6)", color: "#fff",
                        border: "none", borderRadius: "50%", width: 18, height: 18, fontSize: 10,
                        display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer"
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Existing Photos when editing */}
            {isEdit && existingPhotos.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <p style={{ fontSize: 11, color: "var(--taupe)", marginBottom: 6, fontWeight: 600 }}>Existing Uploaded Photos:</p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {existingPhotos.map(ph => {
                    const rawSrc =
                      ph.filepath ||
                      ph.Filepath ||
                      ph.filename ||
                      ph.Filename ||
                      (Array.isArray(ph.photos) ? ph.photos[0] : ph.photos) ||
                      (Array.isArray(ph.Photos) ? ph.Photos[0] : ph.Photos);
                    const src = rawSrc && typeof rawSrc === "string" ? toAbsUrl(rawSrc) : null;
                    return (
                      <div key={ph.photoID} style={{ position: "relative", width: 64, height: 64, borderRadius: 8, overflow: "hidden", border: "1px solid var(--taupe)" }}>
                        {src ? (
                          <img src={src} alt="existing" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        ) : (
                          <div style={{ width: "100%", height: "100%", background: "#eee", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10 }}>No img</div>
                        )}
                        <button
                          type="button"
                          onClick={() => handleDeleteExisting(ph.photoID)}
                          title="Delete photo"
                          style={{
                            position: "absolute", top: 2, right: 2, background: "rgba(192,57,43,.85)", color: "#fff",
                            border: "none", borderRadius: "50%", width: 18, height: 18, fontSize: 10,
                            display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer"
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Short label */}
          <div className="rv-field">
            <label className="rv-label">Description Label</label>
            <input
              className="rv-input"
              placeholder="Short label shown under the item (e.g. 18K Yellow Gold · 0.50 ct Diamond)"
              value={form.descriptionLbl}
              onChange={e => set("descriptionLbl", e.target.value)}
            />
          </div>

          {/* Full description (Rich Text) */}
          <div className="rv-field">
            <label className="rv-label" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Description</span>
              <span style={{ fontSize: 11, color: "var(--taupe)", fontWeight: 400 }}>Rich Text (Colors, Highlights, Font Size, Alignment)</span>
            </label>
            <div style={{
              background: "#fff",
              borderRadius: 8,
              border: "1px solid rgba(185,167,146,.35)",
              overflow: "hidden",
            }}>
              <CatalogueQuillEditor
                initialHtml={form.description}
                onChange={html => set("description", html)}
                placeholder="Full description for the catalogue listing — features, purity details, styling notes…"
              />
            </div>
          </div>

          {/* Comments */}
          <div className="rv-field">
            <label className="rv-label">Internal Comments</label>
            <input
              className="rv-input"
              placeholder="Internal notes (not shown publicly)"
              value={form.comments}
              onChange={e => set("comments", e.target.value)}
            />
          </div>

          {/* Toggles */}
          <div className="rv-toggle-row">
            <div className="rv-field rv-field--row">
              <div>
                <label className="rv-label">Featured</label>
                <p className="rv-field-hint">Highlight this item prominently with star</p>
              </div>
              <button
                type="button"
                className={`rv-toggle ${form.isFeatured ? "rv-toggle--on" : ""}`}
                onClick={() => set("isFeatured", !form.isFeatured)}
              ><span className="rv-toggle-knob" /></button>
            </div>
            <div className="rv-field rv-field--row">
              <div>
                <label className="rv-label">Active</label>
                <p className="rv-field-hint">Visible in public customer catalogue</p>
              </div>
              <button
                type="button"
                className={`rv-toggle ${form.isActive ? "rv-toggle--on" : ""}`}
                onClick={() => set("isActive", !form.isActive)}
              ><span className="rv-toggle-knob" /></button>
            </div>
          </div>

        </div>

        <div className="rv-modal-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="rv-btn rv-btn--primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <span className="rv-spinner" /> : null}
            {isEdit ? "Update Item" : "Create Item & Upload"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   CATALOGUE ITEM FULL VIEW MODAL
══════════════════════════════════════ */
function CatalogueDetailModal({ item, products, onEdit, onManagePhotos, onClose, showToast }) {
  const [photos, setPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(true);
  const [activePhotoIdx, setActivePhotoIdx] = useState(0);

  const product = products.find(p => p.productID === item.productID);

  useEffect(() => {
    if (!item?.catalogueItemID) return;
    setLoadingPhotos(true);
    cataloguePhotoApi.getByItemId(item.catalogueItemID)
      .then(res => {
        const list = Array.isArray(res) ? res : res?.data ?? [];
        setPhotos(list);
      })
      .catch(() => setPhotos([]))
      .finally(() => setLoadingPhotos(false));
  }, [item?.catalogueItemID]);

  const getPhotoSrc = (ph) => {
    if (!ph) return null;
    const rawPath =
      ph.filepath ||
      ph.Filepath ||
      ph.filename ||
      ph.Filename ||
      (Array.isArray(ph.photos) ? ph.photos[0] : ph.photos) ||
      (Array.isArray(ph.Photos) ? ph.Photos[0] : ph.Photos);
    return rawPath && typeof rawPath === "string" ? toAbsUrl(rawPath) : null;
  };

  const activePhoto = photos[activePhotoIdx] || photos.find(p => p.isPrimary) || photos[0];
  const activeSrc = getPhotoSrc(activePhoto);

  return (
    <div className="rv-dialog-backdrop" onClick={onClose} style={{ zIndex: 500 }}>
      <div
        className="rv-dialog rv-dialog--form rv-dialog--wide rv-cat-detail-modal"
        style={{
          maxWidth: 880,
          width: "100%",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          borderRadius: 14,
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="rv-modal-header" style={{ alignItems: "center" }}>
          <div className="rv-modal-icon" style={{ background: "rgba(185,167,146,.2)", color: "var(--taupe-dark)" }}>
            <CatalogueIcon />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <h3 className="rv-modal-title" style={{ margin: 0, fontSize: 18 }}>{item.displayName}</h3>
              {item.isFeatured && (
                <span className="rv-featured-badge"><StarIcon /> Featured</span>
              )}
              <span className={`rv-status-pill ${item.isActive ? "rv-status-pill--active" : "rv-status-pill--inactive"}`}>
                <span className="rv-status-dot" />
                {item.isActive ? "Active in Showcase" : "Inactive / Hidden"}
              </span>
              {item.displaySection && (
                <span className="rv-section-badge">{item.displaySection}</span>
              )}
            </div>
            <p className="rv-modal-sub" style={{ marginTop: 2 }}>
              {product?.productCode ? `${product.productCode} · ` : ""}{product?.productName || "Fine Jewellery Piece"}
            </p>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        {/* Body */}
        <div className="rv-modal-body" style={{ overflowY: "auto", flex: 1, padding: "20px 24px" }}>
          <div className="rv-cat-detail-grid">

            {/* Left: Photos Gallery */}
            <div>
              <div style={{
                width: "100%",
                height: 310,
                background: "#FAF7F2",
                borderRadius: 12,
                border: "1px solid rgba(185,167,146,0.3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
                boxShadow: "0 4px 16px rgba(0,0,0,0.04)"
              }}>
                {loadingPhotos ? (
                  <div className="rv-loader" />
                ) : activeSrc ? (
                  <img
                    src={activeSrc}
                    alt={item.displayName}
                    style={{ width: "100%", height: "100%", objectFit: "contain", padding: 8 }}
                  />
                ) : (
                  <div style={{ textAlign: "center", color: "var(--taupe)", padding: 20 }}>
                    <div style={{ marginBottom: 8, display: "flex", justifyContent: "center" }}><DiamondIcon /></div>
                    <p style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>No Photos Uploaded Yet</p>
                    <p style={{ fontSize: 11.5, margin: "4px 0 0" }}>Add images using the photo manager</p>
                  </div>
                )}

                {activePhoto?.isPrimary && (
                  <span style={{
                    position: "absolute",
                    top: 10,
                    left: 10,
                    background: "rgba(185,167,146,0.92)",
                    color: "#fff",
                    fontSize: 10,
                    fontWeight: 700,
                    padding: "3px 8px",
                    borderRadius: 20,
                    letterSpacing: "0.04em",
                    backdropFilter: "blur(4px)"
                  }}>
                    PRIMARY COVER
                  </span>
                )}
              </div>

              {/* Thumbnails strip */}
              {photos.length > 1 && (
                <div style={{ display: "flex", gap: 8, marginTop: 10, overflowX: "auto", paddingBottom: 4 }}>
                  {photos.map((ph, idx) => {
                    const src = getPhotoSrc(ph);
                    const isSelected = (activePhotoIdx === idx);
                    return (
                      <button
                        key={ph.photoID || idx}
                        type="button"
                        onClick={() => setActivePhotoIdx(idx)}
                        style={{
                          width: 54,
                          height: 54,
                          borderRadius: 8,
                          overflow: "hidden",
                          border: isSelected ? "2px solid var(--taupe-dark)" : "1px solid rgba(185,167,146,0.3)",
                          background: "#FAF7F2",
                          padding: 0,
                          cursor: "pointer",
                          flexShrink: 0,
                          outline: "none",
                          boxShadow: isSelected ? "0 2px 8px rgba(0,0,0,0.12)" : "none",
                          opacity: isSelected ? 1 : 0.7,
                          transition: "all 0.15s ease"
                        }}
                      >
                        {src ? (
                          <img src={src} alt="thumb" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        ) : (
                          <ImageIcon />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              <button
                type="button"
                className="rv-btn rv-btn--ghost"
                onClick={() => onManagePhotos(item)}
                style={{ width: "100%", marginTop: 12, justifyContent: "center", border: "1px dashed rgba(185,167,146,0.5)" }}
              >
                <ImageIcon /> Manage Gallery ({photos.length} photos)
              </button>
            </div>

            {/* Right: Specifications & Internal Comments */}
            <div>
              {/* Description Label / Tagline */}
              {item.descriptionLbl && (
                <div style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--taupe-dark)",
                  marginBottom: 14,
                  padding: "6px 12px",
                  background: "rgba(185,167,146,0.12)",
                  borderRadius: 6,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6
                }}>
                  <TagIcon /> {item.descriptionLbl}
                </div>
              )}

              {/* INTERNAL COMMENTS (Dedicated Admin Section) */}
              <div style={{
                background: item.comments ? "rgba(197, 160, 89, 0.08)" : "#FAF8F5",
                border: "1px solid rgba(197, 160, 89, 0.28)",
                borderLeft: "4px solid #C5A059",
                borderRadius: 8,
                padding: "12px 16px",
                marginBottom: 16,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: "#8B6508", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
                  <CommentIcon /> <span>Internal Comments (Staff Only)</span>
                </div>
                <p style={{
                  margin: 0,
                  fontSize: 13,
                  color: item.comments ? "#333" : "var(--taupe)",
                  fontStyle: item.comments ? "normal" : "italic",
                  lineHeight: 1.5,
                  whiteSpace: "pre-wrap"
                }}>
                  {item.comments || "No internal comments entered for this piece."}
                </p>
              </div>

              {/* Rich Text Catalogue Description */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--taupe)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                  Catalogue Description (Public)
                </div>
                <div style={{
                  background: "#fff",
                  border: "1px solid rgba(185,167,146,0.25)",
                  borderRadius: 8,
                  padding: "12px 16px",
                  maxHeight: 180,
                  overflowY: "auto",
                }}>
                  {item.description ? (
                    <div
                      className="ql-editor"
                      style={{ padding: 0, minHeight: "auto", fontSize: 13, color: "var(--dark)", lineHeight: 1.6 }}
                      dangerouslySetInnerHTML={{
                        __html: /<[a-z][\s\S]*>/i.test(item.description)
                          ? item.description
                          : item.description.replace(/\n/g, "<br/>"),
                      }}
                    />
                  ) : (
                    <em style={{ color: "var(--taupe)", fontSize: 12.5 }}>No detailed description provided.</em>
                  )}
                </div>
              </div>

              {/* Specifications Table */}
              <div style={{
                background: "#FAF7F2",
                borderRadius: 8,
                border: "1px solid rgba(185,167,146,0.2)",
                overflow: "hidden"
              }}>
                <div style={{ padding: "8px 12px", background: "rgba(185,167,146,0.15)", fontSize: 11, fontWeight: 700, color: "var(--taupe)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Product Specifications
                </div>
                <table style={{ width: "100%", fontSize: 12.5, borderCollapse: "collapse" }}>
                  <tbody>
                    <tr style={{ borderBottom: "1px solid rgba(185,167,146,0.15)" }}>
                      <td style={{ padding: "8px 12px", color: "var(--taupe)", width: "40%" }}>Linked Product</td>
                      <td style={{ padding: "8px 12px", fontWeight: 600, color: "var(--dark)" }}>{product?.productName || `Product #${item.productID}`}</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid rgba(185,167,146,0.15)" }}>
                      <td style={{ padding: "8px 12px", color: "var(--taupe)" }}>Product Code</td>
                      <td style={{ padding: "8px 12px", fontFamily: "monospace", fontWeight: 600 }}>{product?.productCode || "—"}</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid rgba(185,167,146,0.15)" }}>
                      <td style={{ padding: "8px 12px", color: "var(--taupe)" }}>Purity / Standard</td>
                      <td style={{ padding: "8px 12px", fontWeight: 600 }}>{product?.purityStandard || "—"}</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid rgba(185,167,146,0.15)" }}>
                      <td style={{ padding: "8px 12px", color: "var(--taupe)" }}>Pricing Type</td>
                      <td style={{ padding: "8px 12px" }}>{product?.pricingType || "—"}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "8px 12px", color: "var(--taupe)" }}>Section / Category</td>
                      <td style={{ padding: "8px 12px", fontWeight: 600 }}>{item.displaySection || "—"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="rv-modal-footer rv-cat-detail-footer">
          <div className="rv-cat-detail-footer-group rv-cat-detail-footer-group--showcase">
            <a
              href={`/catalogue?item=${item.catalogueItemID}`}
              target="_blank"
              rel="noopener noreferrer"
              className="rv-btn rv-btn--ghost"
              style={{ textDecoration: "none" }}
              title="Open customer showcase in new tab"
            >
              <ExternalLinkIcon /> <span>View Showcase</span>
            </a>
            <button
              type="button"
              className="rv-btn rv-btn--ghost"
              onClick={() => {
                const url = `${window.location.origin}/catalogue?item=${item.catalogueItemID}`;
                navigator.clipboard?.writeText(url);
                showToast("Customer showcase link copied!");
              }}
              title="Copy shareable link"
            >
              <CopyIcon /> <span>Copy Link</span>
            </button>
          </div>
          <div className="rv-cat-detail-footer-group rv-cat-detail-footer-group--admin">
            <button
              type="button"
              className="rv-btn rv-btn--primary"
              onClick={() => {
                onClose();
                onEdit(item);
              }}
            >
              <EditIcon /> <span>Edit Item</span>
            </button>
            <button type="button" className="rv-btn rv-btn--ghost" onClick={onClose}>
              <span>Close</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   PHOTO PANEL DRAWER (per row)
══════════════════════════════════════ */
function PhotoPanel({ item, onClose, showToast }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPhotos = async () => {
    setLoading(true);
    try {
      const data = await cataloguePhotoApi.getByItemId(item.catalogueItemID);
      setPhotos(Array.isArray(data) ? data : data?.data ?? []);
    } catch {
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };

 useEffect(() => { loadPhotos(); }, [item.catalogueItemID]);

  return (
    <div className="rv-dialog-backdrop" onClick={onClose} style={{ zIndex: 600 }}>
      <div className="rv-photo-panel" onClick={e => e.stopPropagation()}>
        <div className="rv-photo-panel-header">
          <div className="rv-photo-panel-title-wrap">
            <div className="rv-history-icon"><ImageIcon /></div>
            <div>
              <h3 className="rv-history-title">Photos</h3>
              <p className="rv-history-sub">{item.displayName}</p>
            </div>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>
        <div className="rv-photo-panel-body">
          {loading ? (
            <div className="rv-cat-empty">
              <div className="rv-loader" /><p>Loading photos…</p>
            </div>
          ) : (
            <PhotoManager
              itemID={item.catalogueItemID}
              photos={photos}
              onPhotosChange={loadPhotos}
              showToast={showToast}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN CATALOGUE PAGE
══════════════════════════════════════ */
export default function CataloguePage() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterFeatured, setFilterFeatured] = useState("all");

  const [modal, setModal] = useState(null);
  const [viewModal, setViewModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  const [photoPanel, setPhotoPanel] = useState(null);

  /* ── Resizable columns ── */
  const tableRef = useRef(null);
  const [tableReady, setTableReady] = useState(false);
  // #, Display Name, Product, Section, Featured, Status, Photos, Actions
  useResizableColumns(tableRef, tableReady, [50, 200, 150, 140, 95, 100, 135, 140]);

  /* ── Fetch ── */
  const fetchItems = async () => {
    try {
      setLoading(true);
      setTableReady(false);
      const [itemData, prodData] = await Promise.all([
        catalogueItemApi.getAll(),
        // fetch products for the dropdown — reuse productApi if available
        fetch(`${import.meta.env?.VITE_API_BASE ?? "https://api-dev.revajewellers.ashtamtechnologies.com/api"}/Product`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken") ?? ""}`,
          },
        }).then(r => r.json()).catch(() => []),
      ]);
      setItems(Array.isArray(itemData) ? itemData : itemData?.data ?? []);
      const prodList = Array.isArray(prodData) ? prodData : prodData?.data ?? [];
      setProducts(prodList);
    } catch (err) {
      showToast(err.message || "Failed to load catalogue.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);
  useEffect(() => { if (!loading) setTableReady(true); }, [loading]);

  const showToast = (message, type = "success") => setToast({ message, type });

  const getProductName = (productID) =>
    products.find(p => p.productID === productID)?.productName ?? `Product #${productID}`;

  /* ── Save ── */
  const handleSave = async (formData, pendingFiles = []) => {
    setSaving(true);
    try {
      let itemID = formData.catalogueItemID;
      if (itemID) {
        await catalogueItemApi.update(formData);
      } else {
        const created = await catalogueItemApi.create(formData);
        itemID = created?.catalogueItemID ?? created?.data?.catalogueItemID;
      }

      // If photos were dropped/selected, upload them right away
      if (pendingFiles.length > 0 && itemID) {
        await cataloguePhotoApi.upload({
          catalogueItemID: itemID,
          files: pendingFiles,
          comments: "",
          displayOrder: 1,
          isPrimary: true,
        });
      }

      showToast(formData.catalogueItemID ? "Catalogue item updated." : "Catalogue item & photos created successfully.");
      setModal(null);
      fetchItems();
    } catch (err) {
      showToast(err.message || "Something went wrong.", "error");
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ── */
  const handleDelete = async () => {
    if (!confirm) return;
    try {
      await catalogueItemApi.remove(confirm.id);
      showToast("Catalogue item deleted.");
      setConfirm(null);
      fetchItems();
    } catch (err) {
      showToast(err.message || "Delete failed.", "error");
    }
  };

  /* ── Filtered list ── */
  const filtered = items.filter(item => {
    const prodName = getProductName(item.productID);
    const matchSearch =
      item.displayName?.toLowerCase().includes(search.toLowerCase()) ||
      item.displaySection?.toLowerCase().includes(search.toLowerCase()) ||
      prodName.toLowerCase().includes(search.toLowerCase());
    const matchStatus =
      filterStatus === "all" ||
      (filterStatus === "active" && item.isActive) ||
      (filterStatus === "inactive" && !item.isActive);
    const matchFeatured =
      filterFeatured === "all" ||
      (filterFeatured === "featured" && item.isFeatured) ||
      (filterFeatured === "unfeatured" && !item.isFeatured);
    return matchSearch && matchStatus && matchFeatured;
  });

  return (
    <div className="rv-cat-pg">

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      {confirm && (
        <ConfirmDialog
          name={confirm.name}
          onConfirm={handleDelete}
          onCancel={() => setConfirm(null)}
        />
      )}
      {modal && (
        <CatalogueModal
          initial={modal.data}
          products={products}
          onSave={handleSave}
          onClose={() => setModal(null)}
          saving={saving}
          showToast={showToast}
        />
      )}
      {viewModal && (
        <CatalogueDetailModal
          item={viewModal}
          products={products}
          onEdit={(it) => setModal({ data: it })}
          onManagePhotos={(it) => setPhotoPanel(it)}
          onClose={() => setViewModal(null)}
          showToast={showToast}
        />
      )}
      {photoPanel && (
        <PhotoPanel
          item={photoPanel}
          onClose={() => setPhotoPanel(null)}
          showToast={showToast}
        />
      )}

      {/* ── Topbar ── */}
      <div className="rv-cat-pg-topbar">
        <div className="rv-cat-pg-topbar-left">
          <h2 className="rv-cat-pg-heading">Catalogue</h2>
          <span className="rv-cat-pg-count">{items.length} items</span>
        </div>
        <div className="rv-cat-pg-topbar-actions">
          <a
            href="/catalogue"
            target="_blank"
            rel="noopener noreferrer"
            className="rv-btn rv-btn--ghost"
            style={{ textDecoration: "none" }}
            title="Open customer-facing public showcase in a new tab"
          >
            <ExternalLinkIcon /> <span>View Showcase</span>
          </a>
          <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
            <PlusIcon /> <span>New Item</span>
          </button>
        </div>
      </div>

      {/* ── Summary KPI Cards ── */}
      {!loading && items.length > 0 && (
        <div className="rv-cat-pg-stats">
          {/* Total Items */}
          <div
            className={`rv-cat-pg-stat ${(filterStatus === "all" && filterFeatured === "all") ? "rv-cat-pg-stat--active-filter" : ""}`}
            onClick={() => { setFilterStatus("all"); setFilterFeatured("all"); }}
            title="Click to reset filters and view all catalogue items"
          >
            <div className="rv-cat-pg-stat-icon-wrap" style={{ background: "rgba(185,167,146,0.18)", color: "var(--taupe-dark)" }}>
              <CatalogueIcon />
            </div>
            <div className="rv-cat-pg-stat-body">
              <span className="rv-cat-pg-stat-label">Total Portfolio</span>
              <span className="rv-cat-pg-stat-val">{items.length}</span>
              <span className="rv-cat-pg-stat-sub">All catalogue designs</span>
            </div>
          </div>

          {/* Active Items */}
          <div
            className={`rv-cat-pg-stat ${filterStatus === "active" ? "rv-cat-pg-stat--active-filter" : ""}`}
            onClick={() => setFilterStatus(s => s === "active" ? "all" : "active")}
            title="Click to toggle Active showcase items"
          >
            <div className="rv-cat-pg-stat-icon-wrap" style={{ background: "rgba(39, 174, 96, 0.12)", color: "#27ae60" }}>
              <EyeIcon />
            </div>
            <div className="rv-cat-pg-stat-body">
              <span className="rv-cat-pg-stat-label">Active Showcase</span>
              <span className="rv-cat-pg-stat-val">{items.filter(i => i.isActive).length}</span>
              <span className="rv-cat-pg-stat-sub">Live & visible to customers</span>
            </div>
          </div>

          {/* Featured Highlights */}
          <div
            className={`rv-cat-pg-stat ${filterFeatured === "featured" ? "rv-cat-pg-stat--active-filter" : ""}`}
            onClick={() => setFilterFeatured(f => f === "featured" ? "all" : "featured")}
            title="Click to toggle Featured highlights"
          >
            <div className="rv-cat-pg-stat-icon-wrap" style={{ background: "rgba(212, 175, 55, 0.15)", color: "#B8860B" }}>
              <StarIcon />
            </div>
            <div className="rv-cat-pg-stat-body">
              <span className="rv-cat-pg-stat-label">Featured Pieces</span>
              <span className="rv-cat-pg-stat-val">{items.filter(i => i.isFeatured).length}</span>
              <span className="rv-cat-pg-stat-sub">Highlighted hero jewellery</span>
            </div>
          </div>

          {/* Curated Sections */}
          <div
            className="rv-cat-pg-stat"
            style={{ cursor: "default" }}
            title="Showcase collection sections"
          >
            <div className="rv-cat-pg-stat-icon-wrap" style={{ background: "rgba(143, 123, 103, 0.15)", color: "#8F7B67" }}>
              <LayersIcon />
            </div>
            <div className="rv-cat-pg-stat-body">
              <span className="rv-cat-pg-stat-label">Curated Sections</span>
              <span className="rv-cat-pg-stat-val">{new Set(items.map(i => i.displaySection).filter(Boolean)).size}</span>
              <span className="rv-cat-pg-stat-sub">Bridal, Deluxe, Solitaire…</span>
            </div>
          </div>
        </div>
      )}

      {/* ── Filters ── */}
      <div className="rv-cat-pg-filters">
        <div className="rv-cat-pg-search">
          <SearchIcon />
          <input
            placeholder="Search by name, section or product…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && <button className="rv-cat-pg-search-clear" onClick={() => setSearch("")}><CloseIcon /></button>}
        </div>

        <div className="rv-filter-tabs">
          {["all", "active", "inactive"].map(s => (
            <button key={s} className={`rv-filter-tab ${filterStatus === s ? "rv-filter-tab--active" : ""}`} onClick={() => setFilterStatus(s)}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        <div className="rv-filter-tabs">
          {[
            { key: "all", label: "All" },
            { key: "featured", label: "Featured", icon: <StarIcon /> },
            { key: "unfeatured", label: "Standard" },
          ].map(({ key, label, icon }) => (
            <button key={key} className={`rv-filter-tab ${filterFeatured === key ? "rv-filter-tab--active" : ""}`} onClick={() => setFilterFeatured(key)}>
              {icon && <span style={{ display: "inline-flex", alignItems: "center", marginRight: 4 }}>{icon}</span>}
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Table card ── */}
      <div className="rv-card rv-cat-pg-card">
        {/* Desktop Table View */}
        <div className="rv-desktop-table-view">
          {loading ? (
            <div className="rv-cat-pg-empty">
              <div className="rv-loader" /><p>Loading catalogue…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="rv-cat-pg-empty">
              <div className="rv-empty-icon"><CatalogueIcon /></div>
              <p className="rv-empty-title">No catalogue items found</p>
              <p className="rv-empty-sub">{search ? "Try a different search term." : "Add your first catalogue item to get started."}</p>
              {!search && (
                <button className="rv-btn rv-btn--primary rv-mt-12" onClick={() => setModal({ data: {} })}>
                  <PlusIcon /> New Item
                </button>
              )}
            </div>
          ) : (
            <div className="rv-table-wrap rv-cat-pg-table-wrap">
              <table className="rv-table rv-cat-pg-table" ref={tableRef}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Display Name</th>
                    <th>Product</th>
                    <th>Section</th>
                    <th>Featured</th>
                    <th>Status</th>
                    <th>Photos</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item, i) => (
                    <tr
                      key={item.catalogueItemID}
                      className="rv-cat-pg-row"
                      onClick={() => setViewModal(item)}
                      style={{ cursor: "pointer" }}
                      title="Click to view details"
                    >
                      <td className="rv-cat-pg-idx">{i + 1}</td>
                      <td>
                        <div className="rv-cat-pg-name-cell">
                          <div className="rv-cat-pg-dot" />
                          <div>
                            <span
                              className="rv-cat-pg-name"
                              title="Click to view full details"
                            >
                              {item.displayName}
                            </span>
                            {item.descriptionLbl && (
                              <span className="rv-cat-pg-sublabel">{item.descriptionLbl}</span>
                            )}
                            {item.comments && (
                              <span
                                style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, color: "var(--taupe)", marginTop: 2 }}
                                title={`Internal Comment: ${item.comments}`}
                              >
                                <CommentIcon /> {item.comments.length > 40 ? item.comments.slice(0, 40) + "…" : item.comments}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="rv-cat-pg-product">{getProductName(item.productID)}</span>
                      </td>
                      <td>
                        {item.displaySection
                          ? <span className="rv-section-badge" title={item.displaySection}>{item.displaySection}</span>
                          : <span className="rv-empty-val">—</span>}
                      </td>
                      <td>
                        {item.isFeatured
                          ? <span className="rv-featured-badge"><StarIcon /> Featured</span>
                          : <span className="rv-empty-val">—</span>}
                      </td>
                      <td>
                        <span className={`rv-status-pill ${item.isActive ? "rv-status-pill--active" : "rv-status-pill--inactive"}`}>
                          <span className="rv-status-dot" />
                          {item.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <button
                          className="rv-photo-chip"
                          onClick={(e) => { e.stopPropagation(); setPhotoPanel(item); }}
                          title="Upload, view or manage photos for this item"
                          style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 10px", fontSize: 11.5, fontWeight: 600 }}
                        >
                          <ImageIcon /> Manage Photos
                        </button>
                      </td>
                      <td>
                        <div className="rv-actions" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <button
                            className="rv-action-btn"
                            title="View Full Details"
                            onClick={() => setViewModal(item)}
                            style={{ color: "var(--taupe-dark)" }}
                          >
                            <EyeIcon />
                          </button>
                          <a
                            href={`/catalogue?item=${item.catalogueItemID}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rv-action-btn"
                            title="Open this item directly in public customer showcase"
                            style={{ color: "var(--taupe-dark)", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
                          </a>
                          <button
                            className="rv-action-btn"
                            title="Copy shareable link for this item"
                            onClick={() => {
                              const url = `${window.location.origin}/catalogue?item=${item.catalogueItemID}`;
                              navigator.clipboard?.writeText(url);
                              showToast("Customer showcase link copied!");
                            }}
                            style={{ color: "var(--taupe-dark)" }}
                          >
                            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
                          </button>
                          <button className="rv-action-btn rv-action-btn--edit" title="Edit Item Details" onClick={() => setModal({ data: item })}><EditIcon /></button>
                          <button className="rv-action-btn rv-action-btn--del" title="Delete" onClick={() => setConfirm({ id: item.catalogueItemID, name: item.displayName })}><TrashIcon /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Mobile Cards View */}
        <div className="rv-mobile-cards-view">
          <RvDataCardList
            loading={loading}
            items={filtered}
            emptyIcon={<CatalogueIcon />}
            emptyTitle="No catalogue items found"
            emptySubtitle={search ? "Try a different search term." : "Add your first catalogue item to get started."}
            emptyAction={
              !search && (
                <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
                  <PlusIcon /> New Item
                </button>
              )
            }
            renderCard={(item, i) => (
              <RvDataCard
                key={item.catalogueItemID}
                index={i + 1}
                title={item.displayName}
                subtitle={getProductName(item.productID)}
                badge={
                  <span className={`rv-status-pill ${item.isActive ? "rv-status-pill--active" : "rv-status-pill--inactive"}`}>
                    <span className="rv-status-dot" />
                    {item.isActive ? "Active" : "Inactive"}
                  </span>
                }
                tag={
                  item.isFeatured ? (
                    <span className="rv-featured-badge" style={{ fontSize: 10.5 }}>
                      <StarIcon /> Featured
                    </span>
                  ) : null
                }
                fields={[
                  { label: "Product", value: getProductName(item.productID) },
                  { label: "Section", value: item.displaySection || "—" },
                  ...(item.descriptionLbl ? [{ label: "Description", value: item.descriptionLbl, fullWidth: true }] : []),
                ]}
                onClick={() => setViewModal(item)}
                actions={
                  <div className="rv-actions" style={{ display: "flex", alignItems: "center", gap: 6 }} onClick={e => e.stopPropagation()}>
                    <button
                      className="rv-photo-chip"
                      onClick={() => setPhotoPanel(item)}
                      title="Upload or manage photos"
                      style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 8px", fontSize: 11, fontWeight: 600 }}
                    >
                      <ImageIcon /> Photos
                    </button>
                    <button
                      className="rv-action-btn"
                      title="View Details"
                      onClick={() => setViewModal(item)}
                      style={{ color: "var(--taupe-dark)" }}
                    >
                      <EyeIcon />
                    </button>
                    <button
                      className="rv-action-btn rv-action-btn--edit"
                      title="Edit"
                      onClick={() => setModal({ data: item })}
                    >
                      <EditIcon />
                    </button>
                    <button
                      className="rv-action-btn rv-action-btn--del"
                      title="Delete"
                      onClick={() => setConfirm({ id: item.catalogueItemID, name: item.displayName })}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                }
              />
            )}
          />
        </div>
      </div>

      {!loading && filtered.length > 0 && (
        <p className="rv-resize-hint rv-desktop-table-view"><GripIcon /> Drag column borders to resize</p>
      )}
    </div>
  );
}

/* ── Icons ── */
function PlusIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>; }
function SearchIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>; }
function EditIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>; }
function TrashIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>; }
function CloseIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>; }
function CheckIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>; }
function AlertIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>; }
function ChevronIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>; }
function UploadIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polyline points="16 16 12 12 8 16" /><line x1="12" y1="12" x2="12" y2="21" /><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" /></svg>; }
function ImageIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>; }
function StarIcon() { return <svg viewBox="0 0 24 24" fill="currentColor" stroke="none" style={{ width: 11, height: 11 }}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>; }
function CatalogueIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>; }
function GripIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ width: 13, height: 13 }}><circle cx="9" cy="5" r="1" fill="currentColor" /><circle cx="9" cy="12" r="1" fill="currentColor" /><circle cx="9" cy="19" r="1" fill="currentColor" /><circle cx="15" cy="5" r="1" fill="currentColor" /><circle cx="15" cy="12" r="1" fill="currentColor" /><circle cx="15" cy="19" r="1" fill="currentColor" /></svg>; }
function EyeIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: 14, height: 14 }}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>; }
function LayersIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: 18, height: 18 }}><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></svg>; }
function CopyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="13" height="13" style={{ display: "inline-block", verticalAlign: "middle" }}>
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}
function ExternalLinkIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="13" height="13" style={{ display: "inline-block", verticalAlign: "middle" }}>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}
function DiamondIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="36" height="36" style={{ display: "inline-block", margin: "0 auto" }}>
      <path d="M6 3h12l4 7-10 11L2 10l4-7z" />
      <path d="M2 10h20" />
      <path d="M10 3l-3 7 5 11 5-11-3-7" />
    </svg>
  );
}
function TagIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="13" height="13" style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0 }}>
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
      <line x1="7" y1="7" x2="7.01" y2="7" />
    </svg>
  );
}
function CommentIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="13" height="13" style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0 }}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}